/**
 * Mobile Deployment Schemas
 *
 * Types for app releases, builds, deployments, and version tracking.
 * Mirrors the backend TypeScript interfaces in services/mobile-deployments/shared/types.ts
 */

import { z } from 'zod'
import { MobileAppReadinessSchema } from './mobile-app-readiness'
import { PlatformSchema, type Platform } from './platform'

// --- Enums ---

// Defined in its own leaf module and re-exported here so this path keeps
// working. It had to move: this module imports readiness, readiness now imports
// the push-channel snapshot, and the snapshot is keyed by platform — see
// `./platform` for why that cycle is fatal rather than untidy.
export { PlatformSchema, type Platform } from './platform'

export const AppReleaseTypeSchema = z.enum(['OTA', 'APP_STORE'])
export type AppReleaseType = z.infer<typeof AppReleaseTypeSchema>

export const AppReleaseStatusSchema = z.enum(['DRAFT', 'IN_PROGRESS', 'COMPLETED', 'FAILED', 'CANCELLED'])
export type AppReleaseStatus = z.infer<typeof AppReleaseStatusSchema>

/**
 * The stage machine both lanes share, terminal at RELEASED. Mirrors
 * `AppReleaseTargetStatus` in devops.prisma; the two must be extended together.
 *
 * SUBMITTING / SUBMITTED / IN_REVIEW / APPROVED are the pre-collapse store
 * vocabulary, kept because rows carry them. The stage machine does not produce
 * them; a store's review state now lives in its artifact payload.
 */
export const AppReleaseTargetStatusSchema = z.enum([
  'PENDING',
  'BUILDING',
  'BUILT',
  'DELIVERING',
  'DELIVERED',
  'GATED',
  'RELEASED',
  'FAILED_TRANSIENT',
  'BLOCKED',
  'REFUSED',
  'SUBMITTING',
  'SUBMITTED',
  'IN_REVIEW',
  'APPROVED',
  'FAILED',
  'CANCELLED'
])
export type AppReleaseTargetStatus = z.infer<typeof AppReleaseTargetStatusSchema>

export const AppReleaseEventTypeSchema = z.enum([
  'RELEASE_CREATED',
  'TARGETS_ADDED',
  'RELEASE_CANCELLED',
  'BUILD_STATUS_CHANGED',
  'DEPLOYMENT_STATUS_CHANGED',
  'OTA_PUBLISHED',
  'STATUS_CHANGED'
])
export type AppReleaseEventType = z.infer<typeof AppReleaseEventTypeSchema>

// --- Response Schemas ---

export const RegistryTierSchema = z.enum(['a', 'b'])
export type RegistryTier = z.infer<typeof RegistryTierSchema>

export const BuildProfilesSchema = z.object({
  production: z.string().min(1),
  preview: z.string().min(1),
  development: z.string().min(1)
})
export type BuildProfiles = z.infer<typeof BuildProfilesSchema>

export const RegionMatrixSchema = z.object({
  ios: z.array(z.string().min(2).max(2)),
  android: z.array(z.string().min(2).max(2))
})
export type RegionMatrix = z.infer<typeof RegionMatrixSchema>

/**
 * Per-app provisioning snapshot for the mobile-apps list column + detail tab.
 * Derived (latest run + its live dispatch step counts), so optional: a caller
 * that projects the registry row without the provisioning join still type-checks.
 */
export const MobileAppProvisioningSummarySchema = z.object({
  /** Latest provisioning run for this app, or null if it has never been provisioned. */
  latestRun: z
    .object({
      id: z.string(),
      status: z.enum(['PENDING', 'IN_PROGRESS', 'SUCCEEDED', 'FAILED', 'MANUAL_INTERVENTION_REQUIRED']),
      updatedAt: z.string()
    })
    .nullable(),
  /** Browser steps currently running across the latest run's dispatches. */
  activeStepCount: z.number().int(),
  /** True while any dispatch on the latest run is non-terminal (drives the "running" chip). */
  hasRunningDispatch: z.boolean()
})
export type MobileAppProvisioningSummary = z.infer<typeof MobileAppProvisioningSummarySchema>

/**
 * A single app's standing listing state in ONE store. Mirrors the
 * `StoreListingState` Prisma enum in `apps/backend/prisma/schema/devops.prisma`,
 * whose header carries the full reasoning: one vocabulary for both stores,
 * vendor-specific detail in the note rather than in new members, and store URLs
 * as links that nothing may read as lifecycle.
 *
 * Ordered from "nothing exists" to "gone", which is also the order the admin UI
 * offers them in — `NOT_APPLICABLE` last because it is a deliberate exit from
 * the sequence rather than a stage within it.
 */
export const STORE_LISTING_STATES = [
  'NOT_STARTED',
  'RECORD_CREATED',
  'BUILD_UPLOADED',
  'WAITING_FOR_REVIEW',
  'IN_REVIEW',
  'REJECTED',
  'APPROVED',
  'LIVE',
  'REMOVED',
  'NOT_APPLICABLE'
] as const

export const StoreListingStateSchema = z.enum(STORE_LISTING_STATES)
export type StoreListingState = z.infer<typeof StoreListingStateSchema>

/**
 * Who established a store state. `MANUAL` is the only value written today; an
 * App Store Connect / Play observer would write `OBSERVED`. Mirrors the
 * `StoreStateSource` Prisma enum.
 */
export const StoreStateSourceSchema = z.enum(['MANUAL', 'OBSERVED'])
export type StoreStateSource = z.infer<typeof StoreStateSourceSchema>

/**
 * One store's full state block as it appears on a `MobileAppResponse`.
 *
 * Grouped into a nested object rather than ten flat sibling fields so a UI can
 * render "a store's standing" as one unit and a reader cannot accidentally pair
 * the iOS state with the Android note. `state` is always present (the column is
 * NOT NULL with a `NOT_STARTED` default); everything else is absent until
 * someone has actually set the state.
 */
export const MobileAppStoreListingSchema = z.object({
  state: StoreListingStateSchema,
  source: StoreStateSourceSchema,
  /** Who last moved this store's state — a super-admin id, the internal principal, or a script name. */
  setBy: z.string().optional(),
  setAt: z.coerce.date().optional(),
  /** Free-text why, and the home for vendor-specific detail the shared enum omits. */
  note: z.string().optional()
})
export type MobileAppStoreListing = z.infer<typeof MobileAppStoreListingSchema>

// =============================================================================
// Version Ledger (plan 2026-09-02-2140)
// =============================================================================

/**
 * The listing states (#3179) that put a platform OUT OF SCOPE for the ledger's
 * drift and staleness flags. A platform in one of these states still receives
 * Expo facts — the row is not blanked — but the store-live read is skipped and
 * every flag on that platform is suppressed, because there is nothing at the
 * store to drift from.
 *
 * A subset of `STORE_LISTING_STATES` above, and deliberately a separate list
 * rather than a predicate on the enum: "which states mean not-in-scope" is a
 * ledger judgment, not a property of the listing vocabulary.
 */
export const OUT_OF_SCOPE_STORE_LISTING_STATES = ['NOT_STARTED', 'NOT_APPLICABLE', 'REMOVED'] as const
export type OutOfScopeStoreListingState = (typeof OUT_OF_SCOPE_STORE_LISTING_STATES)[number]

const OUT_OF_SCOPE_STORE_LISTING_STATE_SET: ReadonlySet<string> = new Set(OUT_OF_SCOPE_STORE_LISTING_STATES)

/**
 * Is this platform's listing state one that puts it out of the ledger's scope?
 *
 * The predicate rather than the list, because every consumer was building the
 * same `new Set(...)` beside its own copy of the membership test — three lanes
 * and the flag rules, each one an opportunity for the four to disagree about
 * what "out of scope" means.
 *
 * Takes a widened `string` on purpose: the callers hold a listing state read out
 * of a database column, and an absent one is IN SCOPE (a suppression we cannot
 * prove is better not applied).
 */
export function isOutOfScopeListingState(state: string | null | undefined): boolean {
  return state !== null && state !== undefined && OUT_OF_SCOPE_STORE_LISTING_STATE_SET.has(state)
}

/**
 * The listing states that keep a STORE LANE from running at all (KTD7).
 *
 * A STRICT SUBSET of {@link OUT_OF_SCOPE_STORE_LISTING_STATES}, and the
 * difference is the whole point: `NOT_STARTED` is out of scope for the ledger's
 * DRIFT FLAGS — an app nobody has listed yet must not raise "the build is ahead
 * of the store" on every sweep — but it is NOT a reason to skip the store read.
 * Every app in production carries `NOT_STARTED` today, so gating the lanes on
 * the wider list would leave the board empty for the entire fleet.
 *
 * Only two states mean there is genuinely nothing at the store to ask about:
 * `NOT_APPLICABLE` (we are not shipping this platform) and `REMOVED` (it was
 * taken down). Everything else gets a lane, and an app whose identifiers are
 * missing answers `unreadable` with a reason rather than being skipped (R7) —
 * so the two stores agree and the app counts as unknown rather than healthy.
 *
 * Takes a widened `string` for the same reason the predicate above does: the
 * caller holds a database column, and an absent state is NOT excluded.
 */
export const STORE_LANE_EXCLUDED_LISTING_STATES = ['NOT_APPLICABLE', 'REMOVED'] as const
export type StoreLaneExcludedListingState = (typeof STORE_LANE_EXCLUDED_LISTING_STATES)[number]

const STORE_LANE_EXCLUDED_LISTING_STATE_SET: ReadonlySet<string> = new Set(STORE_LANE_EXCLUDED_LISTING_STATES)

/**
 * Is this platform's listing state one that puts it outside the store lane?
 *
 * The predicate is shared because the rule had been hand-copied into the summary
 * endpoint's denominator and into the web cell that blanks a row, and two copies
 * of "who is on the board" is how the strip ends up counting a row the board
 * refused to render.
 *
 * Takes a widened `string` and treats an ABSENT state as IN SCOPE, for the same
 * reason `isOutOfScopeListingState` does: a suppression we cannot prove is
 * better not applied, and dropping an app out of the denominator is the failure
 * mode that matters here.
 */
export function isStoreLaneExcluded(state: string | null | undefined): boolean {
  return state !== null && state !== undefined && STORE_LANE_EXCLUDED_LISTING_STATE_SET.has(state)
}

/**
 * The three providers the ledger reads, each checked independently. Expo answers
 * for what was built and published; App Store Connect and Play answer for what
 * each store is serving. One lane failing never blanks another lane's facts, so
 * each carries its own outcome and its own check time.
 */
export const DELIVERY_LANES = ['expo', 'appStoreConnect', 'play'] as const
export const DeliveryLaneNameSchema = z.enum(DELIVERY_LANES)
export type DeliveryLaneName = z.infer<typeof DeliveryLaneNameSchema>

/**
 * What the last check on ONE lane amounted to. A check that could not run must
 * never render as a check that passed, and an empty answer is a fact rather
 * than an error — hence four values rather than a boolean:
 *
 *   ok              the provider answered and we recorded what it said
 *   empty           the provider answered and reports nothing published
 *   unreadable      the check failed; `reason` says how (scrubbed)
 *   not-applicable  this lane has no meaning here (no Expo project, no Play
 *                   account, a platform out of scope)
 */
export const DELIVERY_LANE_OUTCOMES = ['ok', 'empty', 'unreadable', 'not-applicable'] as const
export const DeliveryLaneOutcomeSchema = z.enum(DELIVERY_LANE_OUTCOMES)
export type DeliveryLaneOutcome = z.infer<typeof DeliveryLaneOutcomeSchema>

/**
 * The ROW's last outcome overall — three values, derived from the lane map on
 * every stamp so the row-level answer cannot contradict the per-lane one:
 *
 *   observed     at least one lane answered (`ok` or `empty`) and none failed
 *   unreadable   some lane failed; the row is only as good as its worst lane
 *   stale_check  the column's stored default — the row exists and nothing has
 *                ever read a provider for it. Never a reading.
 *
 * The enum lives here rather than in the writer because it is what the API
 * projection carries: one list, so a reader's `=== 'stale_check'` and the
 * writer's derivation cannot drift apart.
 */
export const DELIVERY_CHECK_STATUSES = ['observed', 'unreadable', 'stale_check'] as const
export const DeliveryCheckStatusSchema = z.enum(DELIVERY_CHECK_STATUSES)
export type DeliveryCheckStatus = z.infer<typeof DeliveryCheckStatusSchema>

/**
 * One lane's last check. `lastCheckedAt` moves on EVERY attempt, successful or
 * not — the same split `PublishingAccountObservedStanding` draws between
 * `lastCheckedAt` and `observedAt`, applied per lane instead of per row, because
 * the three lanes run on different cadences and fail independently.
 *
 * `reason` is scrubbed provider text and is only meaningful for `unreadable`.
 */
export const DeliveryLaneSchema = z.object({
  outcome: DeliveryLaneOutcomeSchema,
  /** Scrubbed provider message. Present for `unreadable`; noise anywhere else. */
  reason: z.string().optional(),
  lastCheckedAt: z.coerce.date().optional()
})
export type DeliveryLane = z.infer<typeof DeliveryLaneSchema>

/** The lane map as it appears on a row: absent lane ⇒ never checked. */
export const DeliveryLanesSchema = z.object({
  expo: DeliveryLaneSchema.optional(),
  appStoreConnect: DeliveryLaneSchema.optional(),
  play: DeliveryLaneSchema.optional()
})
export type DeliveryLanes = z.infer<typeof DeliveryLanesSchema>

/**
 * The flags the READ computes from two ledger rows and the thresholds flag.
 * Nothing is stored: a flag is a comparison, and a stored one would go stale the
 * moment either side of the comparison moved.
 *
 * A flag never fires when either fact it compares is absent or unreadable, and
 * every flag is suppressed on a platform whose listing state is out of scope.
 */
export const DELIVERY_FLAGS = [
  /** The iOS and Android store-build versions differ. Every app builds from one
   *  shared `apps/mobile/version.json`, so equal versions are the fleet
   *  invariant and a difference is a real signal, not noise. */
  'platform_version_mismatch',
  /** The newest OTA's runtime version differs from the store build's. The update
   *  is published, not delivered — the ledger never claims device adoption. */
  'ota_runtime_mismatch',
  /** Nothing published over the air for longer than the configured window. */
  'ota_stale',
  /** An in-scope platform whose Expo lane has never observed a published OTA. */
  'ota_never_published',
  /** A lane with no successful check for longer than a multiple of its own
   *  sweep cadence. Distinct from `unreadable`: this one fires on silence. */
  'lane_unverified',
  /** A finished store build is newer than the version the store is serving. */
  'store_build_ahead_of_live'
] as const
export const DeliveryFlagSchema = z.enum(DELIVERY_FLAGS)
export type DeliveryFlag = z.infer<typeof DeliveryFlagSchema>

/**
 * The provenance every fact group carries, mirroring the columns
 * `PublishingAccountObservedStanding` uses and the `source` split #3179
 * introduced for listing state.
 *
 * WHY OBSERVED BEATS MANUAL HERE AND NOT ON LISTING STATE: a version is a fact a
 * provider can prove, so an observation at least as recent as the manual write
 * replaces it and the displacement is recorded as a correction. A listing state
 * is a judgment, so #3179 protects the manual value. The two rules coexist on
 * purpose.
 */
export const DeliveryFactSchema = z.object({
  source: StoreStateSourceSchema,
  /** Who set it — a super-admin id, the internal principal, or an `agent:` / `script:` label. */
  setBy: z.string().optional(),
  setAt: z.coerce.date().optional(),
  note: z.string().optional(),
  /** Last SUCCESSFUL observation of this group. Absent ⇒ only ever set by hand. */
  observedAt: z.coerce.date().optional()
})
export type DeliveryFact = z.infer<typeof DeliveryFactSchema>

/** What was last BUILT for the store (Expo, `FINISHED` + `STORE` distribution). */
export const DeliveryBuildFactSchema = DeliveryFactSchema.extend({
  version: z.string().optional(),
  /** A string on both platforms — Android's numeric version code is derived at read. */
  buildNumber: z.string().optional(),
  runtimeVersion: z.string().optional(),
  easBuildId: z.string().optional(),
  /** The build's own completion time: this group's ordering timestamp (KTD3). */
  completedAt: z.coerce.date().optional()
})
export type DeliveryBuildFact = z.infer<typeof DeliveryBuildFactSchema>

/** What was last PUBLISHED over the air. Published, never "delivered" (R5). */
export const DeliveryOtaFactSchema = DeliveryFactSchema.extend({
  appVersion: z.string().optional(),
  runtimeVersion: z.string().optional(),
  updateGroupId: z.string().optional(),
  /** The SNAPSHOT commit Expo reports, not the source commit (Open Questions). */
  gitCommit: z.string().optional(),
  /** The update group's creation time: this group's ordering timestamp (KTD3). */
  publishedAt: z.coerce.date().optional()
})
export type DeliveryOtaFact = z.infer<typeof DeliveryOtaFactSchema>

/**
 * What the store is SERVING: App Store Connect `READY_FOR_DISTRIBUTION`, or the
 * Play production track. Rollout fractions are not modeled in this release.
 */
export const DeliveryLiveFactSchema = DeliveryFactSchema.extend({
  version: z.string().optional(),
  buildNumber: z.string().optional(),
  /**
   * This group's ordering timestamp: App Store Connect's `createdDate`, and —
   * because Play returns no timestamp at all — the sweep's own read time on
   * Android, where the lane's serialization is what prevents a regression.
   */
  orderingAt: z.coerce.date().optional()
})
export type DeliveryLiveFact = z.infer<typeof DeliveryLiveFactSchema>

/**
 * ONE ledger row: one app on one platform, holding the three fact groups plus
 * the row's own check bookkeeping. A group is ABSENT until something writes it —
 * never an empty object, which a reader could mistake for "we looked and found
 * nothing" (that answer is the lane's `empty` outcome).
 */
export const MobileAppPlatformDeliverySchema = z.object({
  platform: PlatformSchema,
  build: DeliveryBuildFactSchema.optional(),
  ota: DeliveryOtaFactSchema.optional(),
  live: DeliveryLiveFactSchema.optional(),
  lanes: DeliveryLanesSchema.optional(),
  /**
   * The drift and staleness flags the READ computed for this platform (R14).
   *
   * Never stored — a stored flag goes stale the moment either side of its
   * comparison moves — so this is filled in by the read attach
   * (`services/mobile-deployments/ledger`) and by nothing else. An EMPTY array
   * is a real answer ("examined, nothing adrift"); ABSENT means this projection
   * did not compute flags at all, which is what a writer-side or test-side
   * projection of a row looks like.
   */
  flags: z.array(DeliveryFlagSchema).optional(),
  /**
   * Kind-specific extras (update message, branch, artifact URL, roll-back
   * marker) the spine columns deliberately omit. Open, capped and allowlisted at
   * the writer, for the reason `AppArtifact.payload` gives: a new extra must not
   * cost a hand-performed PlanetScale promotion.
   */
  detail: z.record(z.string(), z.any()).optional(),
  /** `'observed' | 'unreadable' | 'stale_check'` — the row's last outcome overall. */
  checkStatus: DeliveryCheckStatusSchema,
  /** Scrubbed (KTD16). Never raw provider text. */
  checkError: z.string().optional(),
  /** Set when a "Verify now" reconcile is enqueued, cleared when it lands. */
  reconcileQueuedAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
})
export type MobileAppPlatformDelivery = z.infer<typeof MobileAppPlatformDeliverySchema>

/**
 * Both platforms' ledger rows as they hang off a `MobileAppResponse`. Either
 * side is absent until that platform's row exists — the reconciler creates rows,
 * the registry does not pre-seed them.
 */
export const MobileAppDeliverySchema = z.object({
  ios: MobileAppPlatformDeliverySchema.optional(),
  android: MobileAppPlatformDeliverySchema.optional()
})
export type MobileAppDelivery = z.infer<typeof MobileAppDeliverySchema>

/** A version plus its optional build number — what `compareVersions` orders. */
export interface ComparableVersion {
  version?: string | null
  buildNumber?: string | null
}

const numericSegments = (version: string | null | undefined): number[] | null => {
  if (!version) return null
  const segments = version.split('.')
  const parsed: number[] = []
  for (const segment of segments) {
    if (!/^\d+$/.test(segment)) return null
    parsed.push(Number(segment))
  }
  return parsed.length > 0 ? parsed : null
}

const numericBuild = (buildNumber: string | null | undefined): number | null => {
  if (!buildNumber || !/^\d+$/.test(buildNumber)) return null
  return Number(buildNumber)
}

/**
 * THE ONE version comparator (KTD15), shared by the ledger's flags and the web
 * table's sort so the two can never disagree about which version is newer.
 *
 * Dot segments compared as integers, so 1.8.19 is above 1.8.9 — which string
 * ordering gets exactly backwards, and which is the real six-week gap this
 * ledger exists to surface. A missing trailing segment counts as zero, and a
 * shorter version is the lower one when everything it does carry is equal.
 * Build number breaks a tie, as an integer where parseable, and never outranks
 * the version.
 *
 * INCOMPARABLE IS NOT LOWEST. A pair where either side has a non-numeric segment
 * (`1.8.9-beta`, `v1.8.9`) or no version at all returns `null`: the caller
 * suppresses the flag and sorts the row as null. Treating it as the lowest
 * version would invent a drift finding out of a value we cannot read. An
 * unreadable BUILD number is only a tiebreak we skip, not a reason to refuse the
 * whole comparison.
 */
const sign = (diff: number): number => (diff > 0 ? 1 : diff < 0 ? -1 : 0)

/** Orders two numeric segment lists; a shorter list is lower when every shared segment ties. */
const compareSegments = (left: number[], right: number[]): number => {
  const depth = Math.max(left.length, right.length)
  for (let i = 0; i < depth; i++) {
    const diff = sign((left[i] ?? 0) - (right[i] ?? 0))
    if (diff !== 0) return diff
  }
  return sign(left.length - right.length)
}

/** Build numbers only break a version tie; an unreadable build number on either side is treated as equal. */
const compareBuilds = (a: ComparableVersion, b: ComparableVersion): number => {
  const leftBuild = numericBuild(a.buildNumber)
  const rightBuild = numericBuild(b.buildNumber)
  if (leftBuild === null || rightBuild === null) return 0
  return sign(leftBuild - rightBuild)
}

export const compareVersions = (a: ComparableVersion, b: ComparableVersion): number | null => {
  const left = numericSegments(a.version)
  const right = numericSegments(b.version)
  if (left === null || right === null) return null
  return compareSegments(left, right) || compareBuilds(a, b)
}

// =============================================================================
// Store versions (SCH-1812)
//
// `MobileAppStoreVersion` is one row per version a STORE has reported for an app
// and platform (KTD1). Everything below is the contract over that table: the
// vendor-neutral state vocabulary, the two raw-state mappings, the board block
// that hangs off a `MobileAppResponse`, and the single derivation the table, the
// summary endpoint, the Fleet tile and the CLI all read (KTD8).
//
// The ledger (`MobileAppDeliverySchema` above) is NOT replaced by this: it still
// answers "what did we build and publish". This answers "what is the store
// serving, and what is in flight behind it".
// =============================================================================

/**
 * Apple's current (non-deprecated) per-version review state, mirrored from
 * `apps/backend/src/interfaces/app-store-connect/release.ts` so web can type a
 * verdict without importing backend code. The deprecated `appStoreState` enum
 * is deliberately absent — do not "restore" it here.
 *
 * It lives in this leaf module rather than in `api/mobile-apps` (where it was
 * defined for SCH-1319, and from where it is still re-exported) because the
 * store-version mapping below has to key on it, and an entity module may not
 * import an API module.
 */
export const ASC_APP_VERSION_STATES = [
  'ACCEPTED',
  'DEVELOPER_REJECTED',
  'IN_REVIEW',
  'INVALID_BINARY',
  'METADATA_REJECTED',
  'PENDING_APPLE_RELEASE',
  'PENDING_DEVELOPER_RELEASE',
  'PREPARE_FOR_SUBMISSION',
  'PROCESSING_FOR_DISTRIBUTION',
  'READY_FOR_DISTRIBUTION',
  'READY_FOR_REVIEW',
  'REJECTED',
  'REPLACED_WITH_NEW_VERSION',
  'WAITING_FOR_EXPORT_COMPLIANCE',
  'WAITING_FOR_REVIEW'
] as const
export const AscAppVersionStateSchema = z.enum(ASC_APP_VERSION_STATES)
export type AscAppVersionState = z.infer<typeof AscAppVersionStateSchema>

/** How Apple releases a version once it is approved. */
export const AscReleaseTypeSchema = z.enum(['MANUAL', 'AFTER_APPROVAL', 'SCHEDULED'])
export type AscReleaseType = z.infer<typeof AscReleaseTypeSchema>

/**
 * Apple's version states, said the way an operator would say them.
 *
 * A map rather than a generic de-underscoring because the two states that
 * matter most read wrong when mechanically transformed: `READY_FOR_DISTRIBUTION`
 * means the release already happened, and `PENDING_APPLE_RELEASE` means Apple
 * owes the next move, not us.
 *
 * THE BOARD AND THE RELEASE DIALOG MUST WORD A STATE THE SAME WAY, which is why
 * these sentences live here rather than beside one of the two screens: the row
 * reaches them through `describeStoreVersionState` below, and the dialog reaches
 * them directly. (The dialog still carries its own identical copy while U9 is
 * unmerged; that copy is to be deleted in favour of this one, not diverged
 * from.)
 */
export const APPLE_VERSION_STATE_PHRASES: Record<AscAppVersionState, string> = {
  // KTD2 maps ACCEPTED onto `live`, so the phrase has to read as released: a
  // sentence saying "not yet ready to release" would contradict the bucket the
  // row is counted in, and the board would argue with its own rollout count.
  ACCEPTED: 'accepted by Apple and released',
  DEVELOPER_REJECTED: 'withdrawn by us',
  IN_REVIEW: 'in review with Apple',
  INVALID_BINARY: 'rejected as an invalid binary',
  METADATA_REJECTED: 'rejected on its metadata',
  PENDING_APPLE_RELEASE: 'approved and waiting for Apple to release it',
  PENDING_DEVELOPER_RELEASE: 'approved and waiting for us to release it',
  PREPARE_FOR_SUBMISSION: 'still being prepared for submission',
  PROCESSING_FOR_DISTRIBUTION: 'being processed for distribution',
  READY_FOR_DISTRIBUTION: 'already released and ready for distribution',
  READY_FOR_REVIEW: 'ready for review but not yet submitted',
  REJECTED: 'rejected by Apple',
  REPLACED_WITH_NEW_VERSION: 'replaced by a newer version',
  WAITING_FOR_EXPORT_COMPLIANCE: 'waiting on export compliance',
  WAITING_FOR_REVIEW: 'waiting for review'
}

const ASC_APP_VERSION_STATE_SET: ReadonlySet<string> = new Set(ASC_APP_VERSION_STATES)

/**
 * The vendor-neutral state every store row carries beside the store's own word
 * (KTD2). Neutral because the board, the rollout count and the precedence rule
 * must not branch on fifteen Apple spellings and four Play ones — and because a
 * state Apple renames costs a mapping line here rather than a schema promotion.
 *
 * `other` is the escape hatch, and it is a real member rather than a null: a
 * state we have no mapping for is a state we HAVE observed, the raw text stays
 * in `vendorState`, and the board renders that raw text. Mapping an unknown
 * state onto a known one is how a board starts lying.
 */
export const STORE_VERSION_STATES = [
  'preparing',
  'submitted',
  'in_review',
  'approved_waiting',
  'scheduled',
  'rolling_out',
  'live',
  'halted',
  'rejected',
  'superseded',
  'withdrawn',
  'other'
] as const
export const StoreVersionStateSchema = z.enum(STORE_VERSION_STATES)
export type StoreVersionState = z.infer<typeof StoreVersionStateSchema>

/**
 * States that mean this version's journey through the store is OVER, for the
 * purpose of picking what is live and what is still in flight.
 *
 * NOT the same rule as KTD3's write-side terminality, which is narrower (it is
 * about which stored states a WEBHOOK may not move, and is Apple-derived only —
 * a Play `live` row can still move). Do not import this one into the writer.
 *
 * `rejected` and `halted` are deliberately absent: a rejected version comes back
 * on the same key when it is resubmitted (AE4) and a halted rollout resumes, so
 * both are still in flight and still worth a row on the board. `other` is absent
 * too — we do not know what it is, and guessing "finished" would hide it.
 */
export const STORE_VERSION_TERMINAL_STATES = ['live', 'superseded', 'withdrawn'] as const
const STORE_VERSION_TERMINAL_STATE_SET: ReadonlySet<string> = new Set(STORE_VERSION_TERMINAL_STATES)

/** Is this row still on its way somewhere — i.e. a candidate for "next"? */
export function isStoreVersionInFlight(state: StoreVersionState): boolean {
  return !STORE_VERSION_TERMINAL_STATE_SET.has(state)
}

/**
 * Which writer produced an observation (R3). Kept per row because only a
 * full-list writer may close a row (KTD4), so a reader has to be able to tell
 * what the last touch actually saw.
 */
export const STORE_VERSION_WRITERS = ['webhook', 'sweep', 'probe'] as const
export const StoreVersionWriterSchema = z.enum(STORE_VERSION_WRITERS)
export type StoreVersionWriter = z.infer<typeof StoreVersionWriterSchema>

/**
 * How a store version's runtime — and so its release line — was known (KTD2 of
 * docs/_archive/2026-10-17-plans-sweep/plans/2026-09-23-2135-feat-app-release-lines-and-school-drafts-plan.md).
 * Listed strongest first, which is also the rank a writer respects: it only
 * replaces a LOWER-ranked source, and an absent runtime ranks below all three.
 *
 *  - `recorded`: the BUILD artifact our EAS webhook stamped with this build number.
 *  - `expo`: Expo's own build record for this build number.
 *  - `inferred`: the marketing `major.minor` mapped to an existing line. A guess,
 *    and every surface must render it as one.
 */
export const STORE_VERSION_RUNTIME_SOURCES = ['recorded', 'expo', 'inferred'] as const
export const StoreVersionRuntimeSourceSchema = z.enum(STORE_VERSION_RUNTIME_SOURCES)
export type StoreVersionRuntimeSource = z.infer<typeof StoreVersionRuntimeSourceSchema>

/** A raw vendor state resolved to the neutral union, with the vendor's own word kept beside it. */
export interface StoreVersionStateMapping {
  storeState: StoreVersionState
  /** The store's own word, VERBATIM. Never normalised — the board renders it for `other`. */
  vendorState: string
}

const APPLE_STORE_VERSION_STATES: Record<AscAppVersionState, StoreVersionState> = {
  PREPARE_FOR_SUBMISSION: 'preparing',
  READY_FOR_REVIEW: 'submitted',
  WAITING_FOR_REVIEW: 'submitted',
  WAITING_FOR_EXPORT_COMPLIANCE: 'submitted',
  PROCESSING_FOR_DISTRIBUTION: 'submitted',
  IN_REVIEW: 'in_review',
  PENDING_DEVELOPER_RELEASE: 'approved_waiting',
  PENDING_APPLE_RELEASE: 'scheduled',
  READY_FOR_DISTRIBUTION: 'live',
  ACCEPTED: 'live',
  REJECTED: 'rejected',
  DEVELOPER_REJECTED: 'rejected',
  METADATA_REJECTED: 'rejected',
  INVALID_BINARY: 'rejected',
  REPLACED_WITH_NEW_VERSION: 'superseded'
}

/**
 * Apple's `appVersionState` → the neutral union (KTD2).
 *
 * All fifteen states Apple currently returns are mapped. Anything else — a state
 * Apple adds tomorrow — maps to `other` and keeps its raw text, which is what
 * lets the board name it without a deploy.
 *
 * The RELEASE ACTION READS THE RAW STATE, never this one (R13): four Apple
 * states mean "approved" in some sense and only `PENDING_DEVELOPER_RELEASE` is
 * ours to release.
 */
export function mapAppleVersionState(vendorState: string): StoreVersionStateMapping {
  const known = ASC_APP_VERSION_STATE_SET.has(vendorState)
    ? APPLE_STORE_VERSION_STATES[vendorState as AscAppVersionState]
    : undefined
  return { storeState: known ?? 'other', vendorState }
}

/**
 * Play's production-track release status → the neutral union (KTD2).
 *
 * `inProgress` splits on the staged-rollout fraction: below 1 it is rolling out,
 * at 1 it is live. A MISSING fraction is NOT 1 — Play omits it on releases that
 * carry no staged rollout, and those arrive as `completed`, so an `inProgress`
 * with no fraction is treated as rolling out and rendered without a percentage
 * rather than claimed as fully rolled out.
 *
 * Play's track API carries no review status at all, so `live` here means
 * "released to the production track"; a release still under Google review reads
 * the same. That is a stated limitation of the vendor, not something a mapping
 * can fix.
 */
export function mapPlayReleaseStatus(vendorState: string, rolloutFraction?: number | null): StoreVersionStateMapping {
  switch (vendorState) {
    case 'draft':
      return { storeState: 'preparing', vendorState }
    case 'inProgress':
      return {
        storeState:
          rolloutFraction !== null && rolloutFraction !== undefined && rolloutFraction >= 1 ? 'live' : 'rolling_out',
        vendorState
      }
    case 'completed':
      return { storeState: 'live', vendorState }
    case 'halted':
      return { storeState: 'halted', vendorState }
    default:
      return { storeState: 'other', vendorState }
  }
}

/**
 * The release request as it is stamped on a row by the release use-case and by
 * nothing else (R15, KTD6).
 *
 * EITHER stamp means "requested" and withdraws the action, so a second click
 * cannot double-release. The unconfirmed arm additionally asks for a re-check:
 * we know a request left here and do not know what Apple did with it. Because
 * the stamp is best-effort, an ABSENT stamp is not proof that no release
 * happened — only proof that we did not record one.
 */
export const StoreVersionReleaseRequestSchema = z.object({
  requestedAt: z.coerce.date().optional(),
  /** The super-admin id or `agent:` / `script:` label that asked. */
  requestedBy: z.string().optional(),
  unconfirmedAt: z.coerce.date().optional()
})
export type StoreVersionReleaseRequest = z.infer<typeof StoreVersionReleaseRequestSchema>

/**
 * ONE observed store version, mirroring `MobileAppStoreVersion` (KTD1).
 *
 * Identity fields are optional for the reason the schema gives: the Apple
 * webhook learns the STATE before anyone learns the version string, so a row can
 * legitimately exist with a state, a key and no version at all (KTD5). A reader
 * that needs a version must handle its absence rather than assume one.
 */
export const MobileAppStoreVersionSchema = z.object({
  /** `MobileAppStoreVersion.publicId`. */
  id: z.string(),
  platform: PlatformSchema,
  /** The STORE's own id: the App Store Connect `appStoreVersion` id, or Play's highest version code. */
  storeVersionKey: z.string(),
  versionString: z.string().optional(),
  buildNumber: z.string().optional(),
  releaseType: z.string().optional(),
  /** Play staged-rollout user fraction, 0-1. Absent is "Play reported none", never 100%. */
  rolloutFraction: z.number().optional(),
  storeState: StoreVersionStateSchema,
  /** The store's own word. What the release action reads (R13). */
  vendorState: z.string(),
  /** This row's ordering timestamp (KTD3) — not a display date. */
  stateOrderingAt: z.coerce.date(),
  observedVia: StoreVersionWriterSchema,
  firstObservedAt: z.coerce.date(),
  observedAt: z.coerce.date(),
  releaseRequestedAt: z.coerce.date().optional(),
  releaseRequestedBy: z.string().optional(),
  releaseRequestUnconfirmedAt: z.coerce.date().optional(),
  /** The runtime this version was built on. Absent = unresolved, which reads as `unknown`, never a count. */
  runtimeVersion: z.string().optional(),
  /** How `runtimeVersion` was known. Present exactly when it is. */
  runtimeSource: StoreVersionRuntimeSourceSchema.optional(),
  /** Vendor extras the spine fields omit, already scrubbed at the writer. */
  detail: z.record(z.string(), z.any()).optional()
})
export type MobileAppStoreVersion = z.infer<typeof MobileAppStoreVersionSchema>

/**
 * Why one lane could not be read, in ONE spelling.
 *
 * `NO_IDENTIFIERS` is the neutral member both stores share: the probe's Apple
 * -specific `NO_APPLE_IDENTIFIERS` maps onto it at the lane, so the derivation
 * has one condition to test rather than one per vendor. It is the only code that
 * changes what the row OFFERS (there is nothing to check), which is why it is a
 * code at all rather than prose in `reason`.
 */
export const STORE_VERSION_LANE_CODES = [
  'NO_IDENTIFIERS',
  'NO_KEY',
  'KEYS_DEAD',
  'APP_NOT_VISIBLE',
  'PROVIDER_ERROR'
] as const
export const StoreVersionLaneCodeSchema = z.enum(STORE_VERSION_LANE_CODES)
export type StoreVersionLaneCode = z.infer<typeof StoreVersionLaneCodeSchema>

/** A delivery lane's last check, plus the machine-readable code the board branches on. */
export const StoreVersionLaneSchema = DeliveryLaneSchema.extend({
  code: StoreVersionLaneCodeSchema.optional()
})
export type StoreVersionLane = z.infer<typeof StoreVersionLaneSchema>

// -----------------------------------------------------------------------------
// The derivation's output vocabulary (KTD8)
// -----------------------------------------------------------------------------
//
// These sit ABOVE the wire block rather than beside the derivation that produces
// them, because the block below CARRIES them (U8b) and a `const` cannot be
// referenced before it is declared. Reading order follows: what the derivation
// decides, then how the response ships it.
//
// Each one is a SCHEMA with its type inferred from it. They were hand-written
// interfaces while the derivation's output stayed server-side; the moment it
// crosses the wire, an interface kept beside a schema is two declarations of one
// shape, and the second edit to either is the one that makes them disagree.

/** The three buckets the rollout count reports (R10). */
export const STORE_VERSION_STANDINGS = ['on', 'not_on', 'unknown'] as const
export const StoreVersionStandingValueSchema = z.enum(STORE_VERSION_STANDINGS)
export type StoreVersionStandingValue = z.infer<typeof StoreVersionStandingValueSchema>

/**
 * WHY an app-and-platform is unknown. A closed union because the filtered board
 * lists unknown rows first WITH their reason (R9): "unknown" on its own is the
 * answer that made this feature necessary.
 */
export const STORE_VERSION_UNKNOWN_REASONS = [
  /** No target version was supplied, so there is nothing to be on or off. */
  'no_target',
  /** The last check failed; `unknownReasonText` carries the scrubbed why. */
  'unreadable',
  /** The app carries no identifiers for this store, so no check can be run. */
  'no_identifiers',
  /** This lane has no meaning for this app at all. */
  'not_applicable',
  /** Nothing has ever successfully read this store for this app. */
  'never_observed',
  /** The last successful observation is older than the staleness threshold. */
  'stale_observation',
  /** A version string on one side will not compare (non-numeric, or absent). */
  'incomparable_version'
] as const
export const StoreVersionUnknownReasonSchema = z.enum(STORE_VERSION_UNKNOWN_REASONS)
export type StoreVersionUnknownReason = z.infer<typeof StoreVersionUnknownReasonSchema>

/** Where one app-and-platform stands against the target version (R10, KTD8). */
export const StoreVersionRolloutSchema = z.object({
  standing: StoreVersionStandingValueSchema,
  /** Set only when `standing` is `unknown`. */
  reason: StoreVersionUnknownReasonSchema.optional(),
  /** The lane's scrubbed message, when the reason came from a failed check. */
  reasonText: z.string().optional(),
  /** The next row's staged-rollout fraction, 0-1, when it is rolling out. */
  rolloutFraction: z.number().optional()
})
export type StoreVersionRollout = z.infer<typeof StoreVersionRolloutSchema>

/**
 * What the row offers to do about this platform (R13).
 *
 * Discriminated on `kind` rather than flattened into optional fields, so a
 * reader that has branched to `release` has `appStoreVersionId` in hand: the id
 * the release action must confirm is exactly what a "maybe present" shape would
 * let a caller forget.
 */
export const StoreVersionActionSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('release'),
    /** The App Store Connect version id the release action must confirm (`expectedAppStoreVersionId`). */
    appStoreVersionId: z.string(),
    versionString: z.string().optional()
  }),
  /**
   * Android (SCH-2920): a production release Play holds back from everyone, a `draft` or a staged
   * `inProgress` rollout below 100%. The action moves it to `completed`. Play's track API carries
   * no review status, so this is NOT "Google approved it": it is "this release is ours to push".
   * Its own arm rather than a second shape of `release`, so every reader that branches on
   * `release` keeps meaning Apple and cannot hand a versionCode to the App Store action.
   */
  z.object({
    kind: z.literal('play_release'),
    /** The versionCode the Play release action must confirm (`expectedVersionCode`). */
    versionCode: z.number().int(),
    /** The status the action must confirm (`expectedStatus`). */
    status: z.enum(['draft', 'inProgress']),
    versionString: z.string().optional()
  }),
  /**
   * Android, managed publishing (SCH-2920): our Release action moved the top production release to
   * `completed`, and Play now holds the approved change in Publishing overview until someone clicks
   * "Publish". The action runs the `play.publish-changes` browser row with `versionString` as its
   * `expectedVersion`, so a version name is required here: without one there is nothing the runner
   * could check the ready-to-publish list against.
   */
  z.object({
    kind: z.literal('play_publish'),
    versionCode: z.number().int(),
    versionString: z.string()
  }),
  z.object({ kind: z.literal('check_now') }),
  z.object({ kind: z.literal('none'), reason: z.enum(['no_play_action', 'no_identifiers', 'not_applicable']) })
])
export type StoreVersionAction = z.infer<typeof StoreVersionActionSchema>

/**
 * The words the row renders. Dates are carried as dates and NOT formatted here:
 * a shared package has no business picking the operator's locale, so the screen
 * composes "could not check since 12 Sep" out of `unreadableText` and
 * `unreadableSince`.
 */
export const StoreVersionBoardSchema = z.object({
  live: MobileAppStoreVersionSchema.optional(),
  next: MobileAppStoreVersionSchema.optional(),
  /** The live version string, when there is one that is readable. */
  liveVersion: z.string().optional(),
  /** `also live: 1.8.19`. Absent when one version or none is live. */
  alsoLiveText: z.string().optional(),
  nextVersion: z.string().optional(),
  /** `next` said the way an operator would say it — the dialog's own phrase for Apple states. */
  nextStateText: z.string().optional(),
  inFlightCount: z.number().int().min(0),
  /** `+2 more in flight`. Absent when one row or none is in flight. */
  inFlightText: z.string().optional(),
  /** `build 1.9.0 done, not yet submitted` (R12, AE5). */
  buildDoneNotSubmitted: z.string().optional(),
  /** `could not check: keys dead` (AE2). Absent unless the last check failed. */
  unreadableText: z.string().optional(),
  /** The lane's scrubbed reason, unformatted. */
  unreadableReason: z.string().optional(),
  /** When that failed check ran — the "since" of the sentence above. */
  unreadableSince: z.coerce.date().optional(),
  /** The last SUCCESSFUL observation: the row's as-of time. */
  asOf: z.coerce.date().optional(),
  releaseRequest: StoreVersionReleaseRequestSchema.optional(),
  /** `release requested by ruben` / `a release may have been requested` (R15). */
  releaseRequestText: z.string().optional()
})
export type StoreVersionBoard = z.infer<typeof StoreVersionBoardSchema>

/**
 * The board block for ONE app and ONE platform: what the derivation resolved out
 * of that platform's rows, carried on the API response so every reader gets the
 * same answer rather than re-deriving it (KTD8).
 *
 * THE WHOLE DERIVATION SHIPS, not a subset of it (U8b). The first cut carried
 * only the rows and the counts, which left the CLI unable to tell `not_on` from
 * `unknown` for a row it had been handed and would have forced the web board to
 * re-run the rule in the browser — the second copy KTD8 exists to forbid. The
 * standing, its reason, the row's action and the rendered phrases are all
 * decided once, on the server, and read here.
 */
export const MobileAppPlatformStoreVersionsSchema = z.object({
  platform: PlatformSchema,
  /**
   * The live row with the newest ordering time — the HEADLINE, not the whole
   * answer. A store can legitimately report more than one live version at once
   * (KTD4), and when it does the standing is decided over all of them while this
   * stays the version the board leads with; `alsoLiveText` names the rest.
   *
   * Absent ⇒ the store serves nothing of ours.
   */
  live: MobileAppStoreVersionSchema.optional(),
  /** The non-terminal row with the newest ordering time, first-observed time breaking a tie. */
  next: MobileAppStoreVersionSchema.optional(),
  /** How many non-terminal rows exist, `next` included. `+N more in flight` is this minus one. */
  inFlightCount: z.number().int().min(0),
  /**
   * The last SUCCESSFUL observation — never the last attempt (that is
   * `lane.lastCheckedAt`). This IS the derivation's `board.asOf`; the board's own
   * key is deliberately not carried a second time under its own name, because
   * one value on the wire twice is one value that can be edited once.
   */
  lastObservedAt: z.coerce.date().optional(),
  lane: StoreVersionLaneSchema.optional(),
  /** The request stamp on whichever in-flight row carries one, hoisted so a reader need not find it. */
  releaseRequest: StoreVersionReleaseRequestSchema.optional(),
  /**
   * Where this app-and-platform stands against the target the request named
   * (R9, R10), and why when the answer is `unknown`.
   *
   * OPTIONAL ON THE WIRE, for the reason `readiness` and `provisioning` are: it
   * is derived per read, and web deploys separately from the API, so a browser
   * running ahead of a backend that predates this field must parse rather than
   * fail. A reader that finds it absent has NOT been told the app is healthy —
   * absent is unknown, never `on` (R19).
   */
  rollout: StoreVersionRolloutSchema.optional(),
  /** What this row offers to do about this platform (R13). Optional for the same reason. */
  action: StoreVersionActionSchema.optional(),
  /** `next` said the way an operator would say it — the release dialog's own phrase (R8). */
  nextStateText: z.string().optional(),
  /** `+2 more in flight`. Absent when one row or none is in flight. */
  inFlightText: z.string().optional(),
  /**
   * `also live: 1.8.19` — the live version(s) behind the headline, which are why
   * an app with `live` at the target can still count as `not_on` (SCH-1882).
   * Absent when one version or none is live. Optional for the same reason
   * `rollout` is: a browser ahead of the API must parse, not fail.
   */
  alsoLiveText: z.string().optional(),
  /** `build 1.9.0 done, not yet submitted` (R12). */
  buildDoneNotSubmitted: z.string().optional(),
  /** `could not check: keys dead`. Absent unless the last check failed. */
  unreadableText: z.string().optional(),
  /** The lane's scrubbed reason, unformatted. */
  unreadableReason: z.string().optional(),
  /** When that failed check ran — the "since" of the sentence above. */
  unreadableSince: z.coerce.date().optional(),
  /** `release requested by ruben` / `a release may have been requested` (R15). */
  releaseRequestText: z.string().optional()
})
export type MobileAppPlatformStoreVersions = z.infer<typeof MobileAppPlatformStoreVersionsSchema>

/**
 * Both platforms' board blocks as they hang off a `MobileAppResponse`. A side is
 * absent only when the projection did not compute it; an app the Apple lane
 * cannot read still gets an `ios` block, carrying the unreadable lane — silence
 * is what would read as health (R19).
 */
export const MobileAppStoreVersionsSchema = z.object({
  ios: MobileAppPlatformStoreVersionsSchema.optional(),
  android: MobileAppPlatformStoreVersionsSchema.optional()
})
export type MobileAppStoreVersions = z.infer<typeof MobileAppStoreVersionsSchema>

// -----------------------------------------------------------------------------
// The shared derivation (KTD8)
// -----------------------------------------------------------------------------

/**
 * How old the last successful observation may be before the app counts as
 * unknown rather than on or not on: one daily store sweep plus margin.
 *
 * Twelve hours of margin, not two: a sweep that slips, a lane that fails once
 * and recovers, and a deploy window all have to fit inside it, or the board
 * spends its mornings reporting unknowns that are merely late.
 */
export const STORE_VERSION_STALENESS_MS = 36 * 60 * 60 * 1000

export interface StoreVersionStanding {
  platform: Platform
  board: StoreVersionBoard
  rollout: StoreVersionRollout
  action: StoreVersionAction
}

export interface StoreVersionThresholds {
  /** Defaults to `STORE_VERSION_STALENESS_MS`. */
  stalenessMs?: number
}

/**
 * Everything the derivation reads, for ONE app and ONE platform.
 *
 * An object rather than the positional argument list the plan sketched: four
 * callers (the list attach, the summary endpoint, the Fleet tile and the CLI)
 * share this, `platform` had to join the list for the action rule to answer at
 * all when there are no rows, and a seven-argument call is one transposition
 * away from a silently wrong board.
 */
export interface StoreVersionStandingInput {
  platform: Platform
  /** Every store-version row for this app and platform — live, in flight and closed. */
  rows: readonly MobileAppStoreVersion[]
  /** The LEDGER's build fact for this platform, which is what R12 compares against. */
  build?: DeliveryBuildFact | null
  /** This store's lane stamp from the ledger row. */
  lane?: StoreVersionLane | null
  /** The version the operator is asking about. Absent ⇒ no standing is claimed (R9). */
  target?: string | null
  /** A parameter, never a clock read: the derivation is pure so four callers agree. */
  now: Date
  thresholds?: StoreVersionThresholds
  /**
   * Android only: when the newest SUCCEEDED `play.publish-changes` dispatch for this app was
   * created, or null when there has been none. Decides whether `play_publish` is still owed.
   * UNDEFINED means the caller did not read it, and then `play_publish` is never offered: a caller
   * that cannot tell a published change from a held one must not ask an operator to publish.
   */
  playPublishedAt?: Date | null
  /**
   * Android only: the version the public Google Play store page served at the last recheck
   * (SCH-3608), when that read found one. It has PRIORITY over the track for Android: with managed
   * publishing the track says `completed` before and after Publish is clicked, and only the page
   * shows the click. Absent leaves the track's answer as it is.
   */
  playStorePage?: PlayStorePageVersion | null
}

/** What the public Play store page served, as the board reads it (SCH-3608). */
export interface PlayStorePageVersion {
  versionString: string
  checkedAt: Date
}

/** The store page and Play's own rows write `1.8.9` and `v1.8.9` for the same version. */
const comparePlayVersions = (row: string | undefined, page: string): number | null =>
  compareVersions({ version: row?.replace(/^v/i, '') }, { version: page.replace(/^v/i, '') })

/**
 * Whether a Play store page reading speaks for a release the track holds. It does not when the
 * release is newer than what the page served and Play first showed it after the reading: the
 * reading never saw it, so whether it was published since is the track's answer until the next
 * recheck. Without this, one recheck pinned the board to its version through every later release
 * (SCH-3608 review).
 */
function playStorePageSawRelease(
  page: PlayStorePageVersion,
  versionString: string | undefined,
  firstSeenAt: Date
): boolean {
  if (firstSeenAt.getTime() <= page.checkedAt.getTime()) return true
  const cmp = comparePlayVersions(versionString, page.versionString)
  return cmp === null || cmp <= 0
}

/**
 * Android's track rows, restated by what the store page serves (SCH-3608). A completed release at the
 * page's version is live, even one the track had marked superseded by a newer completed release; a
 * live release NEWER than the page has not been published, so it is approved and waiting; a live one
 * older than the page is superseded. A row whose version cannot be compared is left as the track has it.
 */
function applyPlayStorePage(
  rows: readonly MobileAppStoreVersion[],
  page: PlayStorePageVersion
): MobileAppStoreVersion[] {
  return rows.map(row => {
    if (row.storeState !== 'live' && !(row.storeState === 'superseded' && row.vendorState === 'completed')) return row
    const cmp = comparePlayVersions(row.versionString, page.versionString)
    if (cmp === null) return row
    if (cmp === 0) return row.storeState === 'live' ? row : { ...row, storeState: 'live' }
    if (row.storeState !== 'live') return row
    return { ...row, storeState: cmp > 0 ? 'approved_waiting' : 'superseded' }
  })
}

/** The neutral phrases, used for every state Apple did not supply a word for. */
const STORE_VERSION_STATE_PHRASES: Record<StoreVersionState, string> = {
  preparing: 'still being prepared',
  submitted: 'submitted and waiting for review',
  in_review: 'in review',
  approved_waiting: 'approved and waiting for us to release it',
  scheduled: 'approved and waiting for the store to release it',
  rolling_out: 'rolling out',
  // Play's track API reports no review status, so this is the strongest true
  // statement available on Android (KTD2) — not "live and approved".
  live: 'released to track; Google review not visible',
  halted: 'rolled out and then halted',
  rejected: 'rejected',
  superseded: 'replaced by a newer version',
  withdrawn: 'no longer listed by the store',
  other: 'in an unrecognised state'
}

/** `0.2` → `20%`. Play reports a fraction; an operator reads a percentage. */
const formatRolloutPercent = (fraction: number): string => `${Math.round(fraction * 100)}%`

/**
 * One row's state, said the way an operator would say it.
 *
 * An Apple-backed row is worded with the RELEASE DIALOG's phrase for its raw
 * state, so the board row and the dialog the operator opens from it cannot
 * describe the same version differently. Everything else falls back to the
 * neutral phrase, and an unmapped state names itself.
 */
export function describeStoreVersionState(
  row: Pick<MobileAppStoreVersion, 'storeState' | 'vendorState' | 'rolloutFraction'>
): string {
  const base = ASC_APP_VERSION_STATE_SET.has(row.vendorState)
    ? APPLE_VERSION_STATE_PHRASES[row.vendorState as AscAppVersionState]
    : row.storeState === 'other'
      ? row.vendorState.toLowerCase().replace(/_/g, ' ')
      : STORE_VERSION_STATE_PHRASES[row.storeState]
  if (row.storeState !== 'rolling_out') return base
  return row.rolloutFraction === undefined || row.rolloutFraction === null
    ? base
    : `${base} ${formatRolloutPercent(row.rolloutFraction)}`
}

/**
 * Newest ordering time wins; the newest FIRST-observed time breaks a tie (KTD8).
 *
 * A real comparator rather than a pairwise "which of these two" test, because
 * `describeAlsoLive` sorts with it: a comparator that answers -1 to both
 * `(a, b)` and `(b, a)` for two genuinely equal rows leaves `Array.sort` free to
 * order them however it likes, and the phrase would reshuffle between two reads
 * of the same rows.
 */
const compareRecency = (a: MobileAppStoreVersion, b: MobileAppStoreVersion): number => {
  const byOrdering = a.stateOrderingAt.getTime() - b.stateOrderingAt.getTime()
  return byOrdering !== 0 ? byOrdering : a.firstObservedAt.getTime() - b.firstObservedAt.getTime()
}

const newerRow = (a: MobileAppStoreVersion, b: MobileAppStoreVersion): MobileAppStoreVersion =>
  compareRecency(a, b) >= 0 ? a : b

const pickNewest = (rows: readonly MobileAppStoreVersion[]): MobileAppStoreVersion | undefined =>
  rows.length === 0 ? undefined : rows.reduce(newerRow)

/**
 * The live versions BEHIND the headline, said the way the board says
 * `+N more in flight` (SCH-1882).
 *
 * The sentence exists to answer "why does this app count as not on when Live
 * already reads 1.9.0": the store is serving something else as well. So it names
 * only what the headline does not already say. A Play production track holding
 * two completed country-targeted releases of the SAME version is the ordinary
 * shape of the two-live-row fact, and `Live 1.9.0` over `also live: 1.9.0` reads
 * as a board arguing with itself — a version string equal to the headline's, and
 * a repeat of one already named, are both dropped.
 *
 * ORDER: distinct version strings newest first, by the same rule that picked the
 * headline, so the phrase does not reshuffle between two reads of the same rows;
 * then a COUNT of the live rows the store has not named yet. Apple's webhook
 * teaches a state before anyone learns the string (KTD5), and those rows are
 * counted rather than named because there is nothing to name and dropping them
 * would hide a version that is being served. The count is deliberately last
 * rather than in recency order: `1 unnamed` has no version to sort by.
 *
 * `undefined` when nothing distinct is left to say.
 */
const describeAlsoLive = (alsoLive: readonly MobileAppStoreVersion[], headlineVersion?: string): string | undefined => {
  if (alsoLive.length === 0) return undefined

  const named: string[] = []
  let unnamed = 0
  for (const row of [...alsoLive].sort((a, b) => -compareRecency(a, b))) {
    if (!row.versionString) unnamed += 1
    else if (row.versionString !== headlineVersion && !named.includes(row.versionString)) {
      named.push(row.versionString)
    }
  }

  const parts = unnamed > 0 ? [...named, `${unnamed} unnamed`] : named
  return parts.length === 0 ? undefined : `also live: ${parts.join(', ')}`
}

const maxDate = (dates: readonly (Date | undefined)[]): Date | undefined =>
  dates.reduce<Date | undefined>((best, d) => (d && (!best || d.getTime() > best.getTime()) ? d : best), undefined)

/**
 * Does the finished store build sit ahead of everything the store has ever shown
 * us (R12, AE5)?
 *
 * Requires a readable observation first: with nothing ever read, "not yet
 * submitted" would be a claim about a store we have not looked at. A single
 * incomparable row also suppresses it — one version string we cannot order is
 * enough to make "newer than every row" unprovable.
 *
 * THE COMPARISON IS VERSION-LEVEL ONLY, deliberately: R12's rule is "the ledger
 * build version compares newer than every store row". Letting the BUILD NUMBER
 * break the tie made a rebuild of the same version (ledger 1.9.0 build 42,
 * store row 1.9.0 build 41 sitting in review) render "build 1.9.0 done, not yet
 * submitted" while 1.9.0 was in fact in review — the sentence claiming a
 * submission gap that does not exist. A row carrying the same version string is
 * proof the store has that version, whatever build it holds.
 */
const buildIsAheadOfEveryRow = (build: DeliveryBuildFact, rows: readonly MobileAppStoreVersion[]): boolean => {
  if (!build.version) return false
  for (const row of rows) {
    const cmp = compareVersions({ version: build.version }, { version: row.versionString })
    if (cmp === null || cmp <= 0) return false
  }
  return true
}

/**
 * THE shared store-version derivation (KTD8): board words, rollout standing and
 * the row's action for one app and one platform.
 *
 * Pure, and pure on purpose — `now` is a parameter, no Prisma type crosses this
 * boundary, and the four callers (list attach, summary endpoint, Fleet tile,
 * `pnpm sj app-versions`) therefore cannot drift into four different answers to
 * "is this app on 1.9.0".
 *
 * THE UNKNOWN BUCKET IS LOAD-BEARING. Never observed, stale, unreadable, no
 * identifiers and unorderable all resolve to `unknown`, never to `on` and never
 * quietly to `not_on`: the failure this board exists to end is a fleet that
 * looks healthy because nobody could read it.
 */
export function storeVersionStanding(input: StoreVersionStandingInput): StoreVersionStanding {
  const { platform, build, lane, target, now } = input
  const reading = platform === 'ANDROID' ? input.playStorePage : undefined
  // A reading that predates a live release newer than it says nothing about that release, so the
  // track decides the board and the action until the next recheck.
  const page =
    reading &&
    input.rows.every(
      row => row.storeState !== 'live' || playStorePageSawRelease(reading, row.versionString, row.firstObservedAt)
    )
      ? reading
      : undefined
  const rows = page ? applyPlayStorePage(input.rows, page) : input.rows
  const stalenessMs = input.thresholds?.stalenessMs ?? STORE_VERSION_STALENESS_MS

  // EVERY live row, not just the newest (SCH-1882). `closeAbsentRows` leaves two
  // live rows open whenever the store's own list still reports both as live
  // (KTD4), and that is a real store fact: a Play production track can hold two
  // completed country-targeted releases, a halted rollout falls back to the
  // previous completed release, and an iOS phased release reads as plain live.
  // The headline is still the newest; the STANDING is decided over all of them,
  // or an app still serving 1.8.19 to half its countries counts as done.
  const liveRows = rows.filter(r => r.storeState === 'live')
  // THE HEADLINE IS THE NEWEST *NAMED* LIVE ROW, falling back to the newest of
  // any kind. Apple's webhook teaches a STATE before anyone learns the version
  // string (KTD5), so the newest live row can be one we cannot name — and
  // leading with it put "nothing live" on the board directly above a sentence
  // naming versions that are live. The unnamed row is not dropped: it still
  // counts in the standing below and still appears in `alsoLiveText`.
  const live = pickNewest(liveRows.filter(r => r.versionString)) ?? pickNewest(liveRows)
  const headline = live ? liveRows.indexOf(live) : -1
  const alsoLive = liveRows.filter((_, index) => index !== headline)
  const inFlight = rows.filter(r => isStoreVersionInFlight(r.storeState))
  const next = pickNewest(inFlight)

  // The last SUCCESSFUL read, which is not the same as the last row: a lane that
  // answered `empty` has read the store and found nothing, and that is an
  // observation — treating it as silence would park an app in `unknown` forever.
  const lastObservedAt = maxDate(rows.map(r => r.observedAt))
  const laneReadable = lane?.outcome === 'ok' || lane?.outcome === 'empty'
  const lastReadableAt = maxDate([lastObservedAt, laneReadable ? lane?.lastCheckedAt : undefined])

  // Hoisted off whichever IN-FLIGHT row carries a stamp, for the same reason the
  // action scans them all: reading it off `next` alone dropped the sentence the
  // moment a newer rejected or preparing row took `next`, leaving the operator a
  // withdrawn button with nothing saying a release had already been asked for.
  // Newest wins when more than one row has been requested.
  const stamped = pickNewest(
    inFlight.filter(r => r.releaseRequestedAt || r.releaseRequestedBy || r.releaseRequestUnconfirmedAt)
  )
  const releaseRequest: StoreVersionReleaseRequest | undefined = stamped
    ? {
        requestedAt: stamped.releaseRequestedAt,
        requestedBy: stamped.releaseRequestedBy,
        unconfirmedAt: stamped.releaseRequestUnconfirmedAt
      }
    : undefined

  const board: StoreVersionBoard = {
    live,
    next,
    liveVersion: live?.versionString,
    alsoLiveText: describeAlsoLive(alsoLive, live?.versionString),
    nextVersion: next?.versionString,
    nextStateText: next ? describeStoreVersionState(next) : undefined,
    inFlightCount: inFlight.length,
    inFlightText: inFlight.length > 1 ? `+${inFlight.length - 1} more in flight` : undefined,
    buildDoneNotSubmitted:
      build && lastReadableAt && buildIsAheadOfEveryRow(build, rows)
        ? `build ${build.version} done, not yet submitted`
        : undefined,
    unreadableText:
      lane?.outcome === 'unreadable' ? `could not check: ${lane.reason ?? 'no reason recorded'}` : undefined,
    unreadableReason: lane?.outcome === 'unreadable' ? lane.reason : undefined,
    unreadableSince: lane?.outcome === 'unreadable' ? lane.lastCheckedAt : undefined,
    asOf: lastReadableAt,
    releaseRequest,
    // Branch on the UNCONFIRMED stamp, not on the absence of the confirmed one:
    // a row carrying only `releaseRequestedBy` (a stamp that half-landed) is a
    // request we know the author of, and reading it out as "may have been
    // requested" would ask an operator to re-check something already attributed.
    releaseRequestText: releaseRequest
      ? releaseRequest.unconfirmedAt && !releaseRequest.requestedAt
        ? 'a release may have been requested; re-check the store'
        : `release requested${releaseRequest.requestedBy ? ` by ${releaseRequest.requestedBy}` : ''}`
      : undefined
  }

  return {
    platform,
    board,
    rollout: deriveRollout({ rows, liveRows, next, lane, target, now, stalenessMs, lastReadableAt }),
    action:
      platform === 'IOS' ? deriveAction(inFlight, lane) : derivePlayAction(rows, lane, input.playPublishedAt, page)
  }
}

const unknownRollout = (reason: StoreVersionUnknownReason, reasonText?: string): StoreVersionRollout => ({
  standing: 'unknown',
  reason,
  ...(reasonText ? { reasonText } : {})
})

/**
 * Where this app-and-platform stands against the target, read over EVERY live
 * row (SCH-1882).
 *
 * `on` is a claim about what the store is SERVING, not about its newest row, so
 * it needs every live row at or above the target. The rule that shipped compared
 * the newest live row alone, which counted an app as done the moment 1.9.0 went
 * live even while a country-targeted 1.8.19 was still being served to real
 * users — exactly the false "done" the fleet rollout count exists to prevent.
 *
 * PRECEDENCE AMONG THE LIVE ROWS: a readable row below the target wins over an
 * unreadable one. `not_on` there is PROVEN, not guessed — one live version we
 * can read and that is behind the target settles the question whatever the
 * string we cannot parse says — and answering `unknown` would throw away a fact
 * we hold. This does not weaken the unknown bucket: unknown guards against an
 * app LOOKING HEALTHY when nobody could read it, and `not_on` is not health.
 * With no readable row below the target, one unparseable string is enough to
 * make `on` unprovable, so the answer is `incomparable_version`.
 */
function deriveRollout(args: {
  rows: readonly MobileAppStoreVersion[]
  liveRows: readonly MobileAppStoreVersion[]
  next?: MobileAppStoreVersion
  lane?: StoreVersionLane | null
  target?: string | null
  now: Date
  stalenessMs: number
  lastReadableAt?: Date
}): StoreVersionRollout {
  const { liveRows, next, lane, target, now, stalenessMs, lastReadableAt } = args
  // The same undefined-AND-null guard `describeStoreVersionState` uses: a row
  // read straight off Prisma before Zod coercion carries `rolloutFraction: null`,
  // and testing only `!== undefined` used to emit that null onto the wire as a
  // fraction. Absent is "Play reported none", never 0% and never 100%.
  const rolloutFraction =
    next?.storeState === 'rolling_out' && next.rolloutFraction !== undefined && next.rolloutFraction !== null
      ? { rolloutFraction: next.rolloutFraction }
      : {}

  // R9: no target chosen ⇒ no count and no filter. Answering `unknown` here
  // rather than `on` keeps a caller that ignores the rule from inventing health.
  if (!target) return unknownRollout('no_target')

  // A failed check outranks everything below it: the rows may be perfectly good
  // and six weeks old, and we cannot tell.
  if (lane?.outcome === 'unreadable') {
    return unknownRollout(lane.code === 'NO_IDENTIFIERS' ? 'no_identifiers' : 'unreadable', lane.reason)
  }
  if (lane?.outcome === 'not-applicable') return unknownRollout('not_applicable', lane.reason)
  if (!lastReadableAt) return unknownRollout('never_observed')
  if (now.getTime() - lastReadableAt.getTime() > stalenessMs) return unknownRollout('stale_observation')

  // Read successfully, and the store serves nothing of ours: that is a fact, and
  // the fact is that this app is not on the target.
  if (liveRows.length === 0) return { standing: 'not_on', ...rolloutFraction }

  let anyBelow = false
  let anyIncomparable = false
  for (const row of liveRows) {
    const cmp = compareVersions({ version: row.versionString, buildNumber: row.buildNumber }, { version: target })
    if (cmp === null) anyIncomparable = true
    else if (cmp < 0) anyBelow = true
  }

  if (anyBelow) return { standing: 'not_on', ...rolloutFraction }
  if (anyIncomparable) return { ...unknownRollout('incomparable_version'), ...rolloutFraction }
  return { standing: 'on', ...rolloutFraction }
}

/**
 * What the row offers (R13).
 *
 * Android offers nothing: Google has no "release it now" verb for a track, so a
 * button there would be a lie. On iOS the RAW state decides, because
 * `approved_waiting` is the only neutral state that maps to a version Apple is
 * holding FOR US — and a version with either request stamp set is already
 * spoken for, confirmed or not.
 *
 * Everything else readable offers "Check now", INCLUDING an unreadable lane: a
 * dead key is exactly the row an operator wants to re-check after rotating it.
 * The exceptions are an app with no identifiers for the store and a lane that
 * has no meaning for the app at all (R6, KTD7): in both there is nothing to
 * check, so offering the check would be an instruction that cannot be followed.
 *
 * THE CANDIDATE IS SCANNED OUT OF EVERY IN-FLIGHT ROW, not read off `next`.
 * `rejected` is deliberately non-terminal (a rejection comes back on the same
 * key, AE4), so it competes for `next` and wins on recency — an approved 1.9.0
 * seen two days ago behind a rejected 1.9.1 seen two hours ago used to withdraw
 * the Release action entirely. That is precisely the "a failed thing hides an
 * approved version" failure R16 exists to end. `next` selection itself is
 * unchanged (KTD8): it is the WORDS the board leads with, not the action.
 */
function deriveAction(inFlight: readonly MobileAppStoreVersion[], lane?: StoreVersionLane | null): StoreVersionAction {
  if (lane?.code === 'NO_IDENTIFIERS') return { kind: 'none', reason: 'no_identifiers' }
  if (lane?.outcome === 'not-applicable') return { kind: 'none', reason: 'not_applicable' }
  const releasable = pickNewest(
    inFlight.filter(
      r => r.vendorState === 'PENDING_DEVELOPER_RELEASE' && !r.releaseRequestedAt && !r.releaseRequestUnconfirmedAt
    )
  )
  if (releasable) {
    return { kind: 'release', appStoreVersionId: releasable.storeVersionKey, versionString: releasable.versionString }
  }
  return { kind: 'check_now' }
}

/**
 * Android's action (SCH-2920). Offered on the newest unrequested production release that Play is
 * holding back: a `draft`, or an `inProgress` staged rollout below 100%. Everything else keeps
 * `no_play_action`, which is what the fleet cell's "finish it in Play Console" marker reads.
 *
 * The rows say what the last sweep saw, not what Play holds now. The release use-case re-reads
 * the track and refuses on any drift, so this only decides whether a button is offered.
 */
function derivePlayAction(
  rows: readonly MobileAppStoreVersion[],
  lane?: StoreVersionLane | null,
  playPublishedAt?: Date | null,
  page?: PlayStorePageVersion | null
): StoreVersionAction {
  if (lane?.code === 'NO_IDENTIFIERS') return { kind: 'none', reason: 'no_identifiers' }
  if (lane?.outcome === 'not-applicable') return { kind: 'none', reason: 'not_applicable' }
  // The HIGHEST versionCode on the whole track, and only when THAT release is releasable: the
  // release action refuses anything but the top of the track, so neither a newer-seen lower row nor
  // a stale draft under a live release may be offered.
  const top = rows
    .map(row => ({ row, versionCode: Number(row.storeVersionKey) }))
    .filter(candidate => Number.isSafeInteger(candidate.versionCode))
    .reduce<{ row: MobileAppStoreVersion; versionCode: number } | undefined>(
      (best, candidate) => (!best || candidate.versionCode > best.versionCode ? candidate : best),
      undefined
    )
  if (!top) return { kind: 'none', reason: 'no_play_action' }
  // A page reading taken before our last Publish predates it and says nothing about it, so the
  // Publish stamp decides then; otherwise Publish would be offered again for a published version.
  const pageIsCurrent = page && !(playPublishedAt && playPublishedAt.getTime() > page.checkedAt.getTime())
  const publish = pageIsCurrent ? playPublishOwedByPage(top.row, page) : playPublishOwed(top.row, playPublishedAt)
  if (publish) return { kind: 'play_publish', versionCode: top.versionCode, versionString: publish }
  if (
    !isPlayReleasableStatus(top.row.vendorState, top.row.rolloutFraction) ||
    top.row.releaseRequestedAt ||
    top.row.releaseRequestUnconfirmedAt
  ) {
    return { kind: 'none', reason: 'no_play_action' }
  }
  const { row: releasable, versionCode } = top
  return {
    kind: 'play_release',
    versionCode,
    status: releasable.vendorState === 'draft' ? 'draft' : 'inProgress',
    versionString: releasable.versionString
  }
}

/**
 * The version name to publish when the top production release is one OUR Release action completed
 * and nothing has published since; null otherwise.
 *
 * Managed publishing is on for every school app, so a release the API moved to `completed` waits in
 * Publishing overview until someone clicks Publish. The track reads the same before and after that
 * click, so the evidence is ours: the row's release-request stamp (confirmed or unconfirmed) says
 * we released it, and a SUCCEEDED `play.publish-changes` dispatch created after the stamp says it
 * has been published. That dispatch does not record which version it published; it is matched by
 * time, which is sound because the runner refuses unless the ready-to-publish list names the version
 * it was given, and reports success only once it has pressed Publish. A run that finds the ready list
 * empty (the change still in Google's review) reports blocked, so Publish stays offered.
 *
 * A release completed by hand in Play Console carries no stamp and is not offered: nothing here can
 * tell whether someone already published it.
 */
function playPublishOwed(row: MobileAppStoreVersion, playPublishedAt: Date | null | undefined): string | null {
  if (playPublishedAt === undefined || !row.versionString) return null
  const completed =
    row.vendorState === 'completed' ||
    (row.vendorState === 'inProgress' && row.rolloutFraction !== undefined && row.rolloutFraction >= 1)
  if (!completed) return null
  const stampedAt = row.releaseRequestedAt ?? row.releaseRequestUnconfirmedAt
  if (!stampedAt) return null
  if (playPublishedAt && playPublishedAt.getTime() > stampedAt.getTime()) return null
  return row.versionString
}

/**
 * Whether the top release still waits in Publishing overview, judged by the store page (SCH-3608):
 * it is completed on the track and newer than what the page serves. Unlike `playPublishOwed` it needs
 * no release stamp of ours, so a release completed by hand in Play Console is offered too.
 */
function playPublishOwedByPage(row: MobileAppStoreVersion, page: PlayStorePageVersion): string | null {
  if (!row.versionString) return null
  const completed =
    row.vendorState === 'completed' ||
    (row.vendorState === 'inProgress' && row.rolloutFraction !== undefined && row.rolloutFraction >= 1)
  if (!completed) return null
  const cmp = comparePlayVersions(row.versionString, page.versionString)
  return cmp !== null && cmp > 0 ? row.versionString : null
}

/**
 * Whether a Play production release status is one the Release action pushes: a `draft`, or an
 * `inProgress` rollout below 100% (a missing fraction counts as below, as `mapPlayReleaseStatus`
 * reads it). One copy of the rule, read by the board and by the backend probe.
 */
export function isPlayReleasableStatus(status: string | null | undefined, rolloutFraction?: number | null): boolean {
  if (status === 'draft') return true
  return status === 'inProgress' && (rolloutFraction === null || rolloutFraction === undefined || rolloutFraction < 1)
}

export const MobileAppResponseSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  displayName: z.string().optional(),
  description: z.string().optional(),
  kind: z.string(),
  schoolId: z.string().optional(),
  schoolName: z.string().optional(),
  bundleId: z.string().optional(),
  expoProjectId: z.string().optional(),
  expoSlug: z.string().optional(),
  androidPackage: z.string().optional(),
  splashBackgroundColor: z.string().optional(),
  splashResizeMode: z.string().optional(),
  /** Android adaptive-icon background layer. Absent ⇒ the build reuses `splashBackgroundColor`. */
  adaptiveIconBackgroundColor: z.string().optional(),
  schoolAppId: z.string().optional(),
  configKey: z.string().optional(),
  buildProfiles: BuildProfilesSchema.optional(),
  regionMatrix: RegionMatrixSchema.optional(),
  tier: RegistryTierSchema.optional(),
  appleTeamId: z.string().optional(),
  /** The Play half of the publishing-account join key, alongside `appleTeamId`. */
  playAccountId: z.string().optional(),
  ascAppId: z.string().optional(),
  googleProjectId: z.string().optional(),
  oneSignalAppId: z.string().optional(),
  appStoreUrl: z.string().optional(),
  playStoreUrl: z.string().optional(),
  /**
   * Per-store listing state (plan 2026-09-03-0330). These are the app's real
   * store standing; `appStoreUrl` / `playStoreUrl` above are LINKS and carry no
   * lifecycle meaning — do not infer one from the other's presence.
   *
   * Optional only so a projection built before this column existed (or one that
   * deliberately omits it) still type-checks; the underlying columns are NOT
   * NULL and the registry list/get responses always populate both.
   */
  iosStore: MobileAppStoreListingSchema.optional(),
  androidStore: MobileAppStoreListingSchema.optional(),
  isActive: z.boolean(),
  /**
   * Why the fleet build config skips this app (absent ⇒ it is emitted). Same rules as the
   * generator, so a dispatch surface can offer only what the workflow will accept (SCH-2084).
   */
  registrySkipReason: z.string().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  /**
   * Computed per-capability delivery readiness (plan 2026-08-15-001 U5).
   * Optional because it is derived, not stored — a caller that projects the
   * registry row without the readiness join still type-checks.
   */
  readiness: MobileAppReadinessSchema.optional(),
  /**
   * Provisioning snapshot for the list column + detail tab (2026-08-19).
   * Optional/derived — present on the list projection, absent on bare reads.
   */
  provisioning: MobileAppProvisioningSummarySchema.optional(),
  /**
   * The Version Ledger for both platforms (plan 2026-09-02-2140): what was last
   * built, what was last published over the air, and what each store is serving.
   *
   * Read from the database only — no provider call happens on a page load, so a
   * projection that skips the ledger join is a cheaper read, not a wrong one,
   * and the field stays optional for it.
   */
  delivery: MobileAppDeliverySchema.optional(),
  /**
   * What each STORE is serving and what is in flight behind it (SCH-1812), as
   * the shared derivation resolved it out of `MobileAppStoreVersion` rows.
   *
   * Distinct from `delivery` above, which answers what we BUILT and PUBLISHED.
   * The two disagree on purpose whenever a build is done and not yet submitted,
   * and that disagreement is the board's whole point (R12).
   *
   * Derived and optional: a projection that skips the store-version join is a
   * cheaper read, not a wrong one.
   */
  storeVersions: MobileAppStoreVersionsSchema.optional()
})
export type MobileAppResponse = z.infer<typeof MobileAppResponseSchema>

export const GitBranchResponseSchema = z.object({
  name: z.string(),
  commitSha: z.string(),
  isDefault: z.boolean()
})
export type GitBranchResponse = z.infer<typeof GitBranchResponseSchema>

/**
 * A release ref an OTA or build can be dispatched from: a `release/mobile-<x.y>`
 * backport branch or a `mobile/v<version>` tag (docs/platform-releases/versioning.md).
 * `name` is the short ref (no `refs/heads/` / `refs/tags/`), which is what
 * GitHub's workflow_dispatch takes as `ref`.
 */
export const GitReleaseRefSchema = z.object({
  name: z.string(),
  kind: z.enum(['branch', 'tag']),
  commitSha: z.string()
})
export type GitReleaseRef = z.infer<typeof GitReleaseRefSchema>

export const AppReleaseEventResponseSchema = z.object({
  id: z.string(),
  eventType: z.string(),
  /**
   * The sentence the writer composed, e.g. "GISSV iOS: Building -> Built".
   *
   * Written at transition time (SCH-1297, KTD8) because that is the only moment
   * the app name, the platform and both statuses are in one place. Absent on
   * historical rows, which the reader still has to render — it falls back to a
   * sentence built from `eventData.previousStatus` / `.newStatus` and the
   * target it resolves out of the release.
   */
  summary: z.string().optional(),
  eventData: z.any().optional(),
  /** The RAW actor string — a Clerk `sub` for a person, a fixed name for anything else. */
  actor: z.string(),
  /** `actor` resolved to a human-readable name at read time; absent when it resolves to nothing. */
  actorLabel: z.string().optional(),
  /** What KIND of principal acted, so a webhook row does not read as a person's doing. */
  actorType: z.string().optional(),
  occurredAt: z.coerce.date(),
  targetId: z.string().optional()
})
export type AppReleaseEventResponse = z.infer<typeof AppReleaseEventResponseSchema>

/** What a provider produced for a target, discriminated by kind. */
export const AppArtifactKindSchema = z.enum(['BUILD', 'OTA_UPDATE', 'STORE_SUBMISSION'])
export type AppArtifactKind = z.infer<typeof AppArtifactKindSchema>

/** Who produced it. Distinct from the EXECUTOR that ran the work. */
export const AppArtifactProviderSchema = z.enum(['EAS', 'APP_STORE_CONNECT', 'GOOGLE_PLAY', 'GITHUB'])
export type AppArtifactProvider = z.infer<typeof AppArtifactProviderSchema>

/**
 * One artifact record. Replaces the separate build and deployment projections:
 * a target that builds and then submits carries two of these, and a reader that
 * wants "the build" filters by kind rather than reading a differently-shaped
 * field.
 *
 * `payload` is deliberately open — the kind-specific fields live there so a new
 * one does not cost a hand-performed schema promotion.
 */
export const AppArtifactResponseSchema = z.object({
  id: z.string(),
  kind: AppArtifactKindSchema,
  provider: AppArtifactProviderSchema,
  providerId: z.string().optional(),
  platform: PlatformSchema,
  dispatchId: z.string(),
  payload: z.record(z.string(), z.any()).optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
})
export type AppArtifactResponse = z.infer<typeof AppArtifactResponseSchema>

export const AppReleaseTargetResponseSchema = z.object({
  id: z.string(),
  platform: PlatformSchema,
  status: AppReleaseTargetStatusSchema,
  startedAt: z.coerce.date().optional(),
  completedAt: z.coerce.date().optional(),
  errorMessage: z.string().optional(),
  otaUpdateId: z.string().optional(),
  otaUpdateBranch: z.string().optional(),
  /** The batch this target was dispatched in. A wave is the set sharing a key. */
  waveKey: z.string().optional(),
  /** Which executor implementation produced this target's artifacts. */
  executor: z.string().optional(),
  isCanary: z.boolean().optional(),
  attemptCount: z.number().optional(),
  /** 'TRANSIENT' | 'STRUCTURAL' — set only once a failure has been classified. */
  failureClass: z.string().optional(),
  /** Machine-readable, provider-shaped. The human sentence is assembled at render time. */
  failureReason: z.string().optional(),
  goLiveCondition: z.string().optional(),
  mobileApp: z.object({
    id: z.string(),
    name: z.string().optional(),
    /**
     * School key (`mobile-app-config.json` top-level key, derived from `schoolAppId`) — the join to
     * icons and branding. Named `configKey` for contract stability; it no longer reads that column.
     */
    configKey: z.string().optional()
  }),
  artifacts: z.array(AppArtifactResponseSchema).optional()
})
export type AppReleaseTargetResponse = z.infer<typeof AppReleaseTargetResponseSchema>

/** One app a release targets, as the list/detail responses summarise it. */
export const AppReleaseAppSummarySchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  configKey: z.string().optional()
})
export type AppReleaseAppSummary = z.infer<typeof AppReleaseAppSummarySchema>

export const AppReleaseResponseSchema = z.object({
  id: z.string(),
  releaseType: AppReleaseTypeSchema,
  status: AppReleaseStatusSchema,
  title: z.string().optional(),
  description: z.string().optional(),
  gitCommitHash: z.string().optional(),
  gitBranch: z.string().optional(),
  initiatedBy: z.string(),
  /** `initiatedBy` resolved to a readable name; absent when it resolves to nothing. */
  initiatedByLabel: z.string().optional(),
  initiatedAt: z.coerce.date(),
  completedAt: z.coerce.date().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  version: z
    .object({
      id: z.string(),
      number: z.string()
    })
    .optional(),
  /**
   * Derived, not stored. Everything below is summarised from `targets` and their
   * artifacts by `summarizeRelease` in the backend mapper — a release row carries
   * none of it, so every field is optional and absent when nothing is derivable.
   */
  /** Build profile: BUILD `payload.variant`, else OTA `payload.channel`, else parsed from the title. */
  variant: z.string().optional(),
  /** Distinct target platforms, IOS before ANDROID. */
  platforms: z.array(PlatformSchema).optional(),
  /** Distinct target apps in target order. */
  apps: z.array(AppReleaseAppSummarySchema).optional(),
  appCount: z.number().optional(),
  /** Marketing version: artifact `payload.appVersion`, else the snapshot manifest's resolved VERSION. */
  appVersion: z.string().optional(),
  /** Expo runtime version, where an artifact recorded one. */
  runtimeVersion: z.string().optional(),
  targets: z.array(AppReleaseTargetResponseSchema).optional()
})
export type AppReleaseResponse = z.infer<typeof AppReleaseResponseSchema>

export const VersionDeploymentPlatformStatusSchema = z.object({
  status: AppReleaseTargetStatusSchema,
  releaseType: AppReleaseTypeSchema,
  releaseId: z.string()
})
export type VersionDeploymentPlatformStatus = z.infer<typeof VersionDeploymentPlatformStatusSchema>

export const VersionDeploymentAppStatusSchema = z.object({
  mobileApp: z.object({
    id: z.string(),
    name: z.string().optional()
  }),
  platforms: z.object({
    IOS: VersionDeploymentPlatformStatusSchema.optional(),
    ANDROID: VersionDeploymentPlatformStatusSchema.optional()
  })
})
export type VersionDeploymentAppStatus = z.infer<typeof VersionDeploymentAppStatusSchema>

export const VersionDeploymentStatusResponseSchema = z.object({
  version: z.object({
    id: z.string(),
    number: z.string()
  }),
  apps: z.array(VersionDeploymentAppStatusSchema)
})
export type VersionDeploymentStatusResponse = z.infer<typeof VersionDeploymentStatusResponseSchema>
